/**
 * 财务配置和付费节点 API 服务
 */
import { get, post, put, del } from '../utils/request';
import type {
  CaseFinanceConfig,
  CaseFinanceFullConfig,
  CaseFinanceConfigCreate,
  CaseFinanceConfigUpdate,
  PaymentMilestone,
  PaymentMilestoneWithRecords,
  PaymentMilestoneCreate,
  PaymentMilestoneUpdate,
  PaymentMilestoneBatchCreate,
  PaymentMilestoneProgress,
} from '../types/financeConfig';

// ==================== 案件财务配置 API ====================

/**
 * 创建或更新案件财务配置
 */
export const createOrUpdateFinanceConfig = async (
  caseId: number,
  data: CaseFinanceConfigCreate
): Promise<CaseFinanceConfig> => {
  return post<CaseFinanceConfig>(`/cases/${caseId}/finance/config`, data);
};

/**
 * 获取案件财务配置（包含付费节点列表和进度统计）
 */
export const getFinanceConfig = async (caseId: number): Promise<CaseFinanceFullConfig> => {
  return get<CaseFinanceFullConfig>(`/cases/${caseId}/finance/config`);
};

/**
 * 删除案件财务配置
 */
export const deleteFinanceConfig = async (caseId: number): Promise<void> => {
  return del<void>(`/cases/${caseId}/finance/config`);
};

// ==================== 付费节点 API ====================

/**
 * 获取案件的付费节点列表
 */
export const getPaymentMilestones = async (caseId: number): Promise<PaymentMilestone[]> => {
  return get<PaymentMilestone[]>(`/cases/${caseId}/finance/milestones`);
};

/**
 * 创建付费节点
 */
export const createPaymentMilestone = async (
  caseId: number,
  data: PaymentMilestoneCreate
): Promise<PaymentMilestone> => {
  return post<PaymentMilestone>(`/cases/${caseId}/finance/milestones`, data);
};

/**
 * 批量创建付费节点
 */
export const batchCreateMilestones = async (
  caseId: number,
  data: PaymentMilestoneBatchCreate
): Promise<PaymentMilestone[]> => {
  return post<PaymentMilestone[]>(`/cases/${caseId}/finance/milestones/batch`, data);
};

/**
 * 获取单个付费节点详情（包含关联的财务记录统计）
 */
export const getPaymentMilestone = async (
  caseId: number,
  milestoneId: number
): Promise<PaymentMilestoneWithRecords> => {
  return get<PaymentMilestoneWithRecords>(`/cases/${caseId}/finance/milestones/${milestoneId}`);
};

/**
 * 更新付费节点
 */
export const updatePaymentMilestone = async (
  caseId: number,
  milestoneId: number,
  data: PaymentMilestoneUpdate
): Promise<PaymentMilestone> => {
  return put<PaymentMilestone>(`/cases/${caseId}/finance/milestones/${milestoneId}`, data);
};

/**
 * 删除付费节点
 */
export const deletePaymentMilestone = async (
  caseId: number,
  milestoneId: number
): Promise<void> => {
  return del<void>(`/cases/${caseId}/finance/milestones/${milestoneId}`);
};

/**
 * 获取付费节点进度统计
 */
export const getMilestoneProgress = async (caseId: number): Promise<PaymentMilestoneProgress> => {
  return get<PaymentMilestoneProgress>(`/cases/${caseId}/finance/milestones/progress`);
};

/**
 * 刷新付费节点状态（根据关联的财务记录重新计算）
 */
export const refreshMilestoneStatus = async (
  caseId: number,
  milestoneId: number
): Promise<PaymentMilestone> => {
  return post<PaymentMilestone>(`/cases/${caseId}/finance/milestones/${milestoneId}/refresh`, {});
};

// ==================== 线索财务配置 API ====================

/**
 * 创建或更新线索财务配置
 */
export const createOrUpdateLeadFinanceConfig = async (
  leadId: number,
  data: import('../types/financeConfig').LeadFinanceConfigCreate
): Promise<import('../types/financeConfig').LeadFinanceConfig> => {
  return post(`/leads/${leadId}/finance/config`, data);
};

/**
 * 获取线索财务配置（包含付费节点列表和进度统计）
 */
export const getLeadFinanceConfig = async (
  leadId: number
): Promise<import('../types/financeConfig').LeadFinanceFullConfig> => {
  return get(`/leads/${leadId}/finance/config`);
};

/**
 * 删除线索财务配置
 */
export const deleteLeadFinanceConfig = async (leadId: number): Promise<void> => {
  return del(`/leads/${leadId}/finance/config`);
};

// ==================== 线索付费节点 API ====================

/**
 * 获取线索的付费节点列表
 */
export const getLeadPaymentMilestones = async (leadId: number): Promise<PaymentMilestone[]> => {
  return get(`/leads/${leadId}/finance/milestones`);
};

/**
 * 创建线索付费节点
 */
export const createLeadPaymentMilestone = async (
  leadId: number,
  data: import('../types/financeConfig').LeadPaymentMilestoneCreate
): Promise<PaymentMilestone> => {
  return post(`/leads/${leadId}/finance/milestones`, data);
};

/**
 * 批量创建线索付费节点
 */
export const batchCreateLeadMilestones = async (
  leadId: number,
  data: import('../types/financeConfig').LeadPaymentMilestoneBatchCreate
): Promise<PaymentMilestone[]> => {
  return post(`/leads/${leadId}/finance/milestones/batch`, data);
};

/**
 * 获取单个线索付费节点详情
 */
export const getLeadPaymentMilestone = async (
  leadId: number,
  milestoneId: number
): Promise<PaymentMilestoneWithRecords> => {
  return get(`/leads/${leadId}/finance/milestones/${milestoneId}`);
};

/**
 * 更新线索付费节点
 */
export const updateLeadPaymentMilestone = async (
  leadId: number,
  milestoneId: number,
  data: PaymentMilestoneUpdate
): Promise<PaymentMilestone> => {
  return put(`/leads/${leadId}/finance/milestones/${milestoneId}`, data);
};

/**
 * 删除线索付费节点
 */
export const deleteLeadPaymentMilestone = async (
  leadId: number,
  milestoneId: number
): Promise<void> => {
  return del(`/leads/${leadId}/finance/milestones/${milestoneId}`);
};

/**
 * 获取线索付费节点进度统计
 */
export const getLeadMilestoneProgress = async (
  leadId: number
): Promise<PaymentMilestoneProgress> => {
  return get(`/leads/${leadId}/finance/milestones/progress`);
};

/**
 * 刷新线索付费节点状态
 */
export const refreshLeadMilestoneStatus = async (
  leadId: number,
  milestoneId: number
): Promise<PaymentMilestone> => {
  return post(`/leads/${leadId}/finance/milestones/${milestoneId}/refresh`, {});
};
